import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { CustomizedTreeView } from "./components/CustomizedComponents";
import RecursiveTreeView from "./components/RecursiveTreeView";
import { useSelector, useDispatch } from "react-redux";
import { getAllDeviceGroupsSelector } from "../../GlobalData/DeviceGroups/selectors";
import { createDeviceGroup, deleteDeviceGroup, updateDeviceGroup } from "../../GlobalData/DeviceGroups/actions";
import RightClickMenu from "./components/RightClickMenu";
import { deviceGroupsService } from "client-app-core";
import PropTypes from "prop-types";
import { controlClicked } from "../../SharedActions/deviceProfileActions";
import { openDialog } from "orion-components/AppState/Actions";
import { compositeDeviceService } from "client-app-core";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import { loadProfile } from "orion-components/ContextPanel/Actions";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import { generateDirStyles } from "../../SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { getTranslation, Translate } from "orion-components/i18n";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

const baseStyles = {
	treeSx: { flexGrow: 1, overflowY: "auto", color: "#FFFFFF", overflowX: "hidden", height: "100%" }
};

const DeviceTree = ({
	addToDeviceGroup,
	getSelectedNodes,
	readOnly,
	selectedBatchColors = [],
	search = "",
	isForFacilitiesFloorPlan,
	getSelectedDevice,
	setFields,
	allowDeviceSelection,
	ignoreEmptyGroups,
	entitySelectionFilters,
	autoExpandMatchingNodes,
	customStyles,
	autoExpandNodes,
	allowDeviceGroupCommands,
	addUngroupedDevicesToRoot,
	expandRootNodeByDefault
}) => {
	const [expandedNodes, setExpandedNodes] = useState([]);
	const [selectedNodeForEdit, setSelectedNodeForEdit] = useState(null);
	const [contextMenu, setContextMenu] = useState(null);
	const [selectedNode, setSelectedNode] = useState(null);
	const [addingNewNode, setAddingNewNode] = useState(false);
	const [rightClickFor, setRightClickFor] = useState("group");
	const [compositeDeviceRolledUpCommands, setCompositeDeviceRolledUpCommands] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [basicDeviceCommands, setBasicDeviceCommands] = useState([]);
	const [deviceGroupRolledUpCommands, setDeviceGroupRolledUpCommands] = useState([]);

	const canManageCamerasApp = useSelector((state) => canManageByApplication(state, "cameras-app", "manage"));
	const dir = useSelector((state) => getDir(state));
	const styles = generateDirStyles(baseStyles, dir);

	const deviceGroups = useSelector(
		(state) =>
			getAllDeviceGroupsSelector(state, {
				deviceStateColor: selectedBatchColors,
				searchString: search,
				ignoreDevices: addToDeviceGroup,
				ignoreEmptyGroups: ignoreEmptyGroups,
				addUngroupedDevicesToRoot: addUngroupedDevicesToRoot,
				...entitySelectionFilters
			}),
		isEqual
	);
	const dispatch = useDispatch();

	const getSelectedMenuItem = (menuItem, node) => {
		switch (menuItem) {
			case "Edit":
				setAddingNewNode(false);
				setSelectedNodeForEdit(selectedNode.id);
				break;
			case "Add-sub-group":
				{
					// Prevent creating another subgroup if one is already being added
					if (selectedNodeForEdit) {
						setContextMenu(null);
						return;
					}

					setAddingNewNode(true);
					if (selectedNode?.children && selectedNode.children.length < 10) {
						addNewNode(selectedNode?.id);
					} else {
						dispatch(
							createUserFeedback(
								getTranslation("global.deviceManager.deviceTree.maxNestedNodesReached"),
								null,
								true
							)
						);
					}
				}

				break;
			case "Delete":
				deviceGroupsService.deleteDeviceGroup(selectedNode.id, (err) => {
					if (err) {
						console.error(err, "unable to delete device group");
						dispatch(
							createUserFeedback(
								getTranslation("global.deviceManager.deviceTree.deviceGroupDeletionFailed"),
								null,
								true
							)
						);
					} else {
						dispatch(deleteDeviceGroup(selectedNode.id));
					}
				});
				break;

			case "addToGroup":
				{
					setSelectedRows([{ type: selectedNode?.entityType, id: selectedNode?.id }]);
					dispatch(openDialog("addToDeviceGroup-dialog"));
				}
				break;

			case "openItem":
				{
					dispatch(loadProfile(selectedNode?.id, selectedNode?.name, selectedNode?.entityType, "profile"));
					if (selectedNode?.entityType === "compositeDevice") handleExpandNodes(selectedNode.id);
				}

				break;

			case "assignDeviceType":
				dispatch(openDialog("assignDeviceType-dialog"));
				break;
			case "addToCompositeDevice":
				setSelectedNode([{ type: selectedNode?.entityType, id: selectedNode?.id }]);
				dispatch(openDialog("addToCompositeDevice-dialog"));
				break;
			case "removeFromCompositeDevice":
				{
					const { entityType, id, compositeDeviceId, name } = node || selectedNode || {};
					const devices = [{ type: entityType, id: id }];

					const body = { devices };
					compositeDeviceService.removeCompositeDevices(compositeDeviceId, body, (err, res) => {
						if (err || res?.success === false) {
							const errorMessage = {
								message: getTranslation(
									"global.deviceManager.deviceTree.removalFailedFromCompositeDevice",
									{ primaryValue: name }
								),
								error: err
							};
							console.error("Error Details:", errorMessage);
							dispatch(
								createUserFeedback(
									getTranslation(
										"global.deviceManager.deviceTree.failedRemovingFromCompositeDevice",
										{ primaryValue: name }
									),
									null,
									true
								)
							);
							return;
						} else {
							dispatch(
								createUserFeedback(
									getTranslation(
										"global.deviceManager.deviceTree.removedSuccessfullyFromCompositeDevice",
										{ primaryValue: name }
									),
									null,
									false
								)
							);
						}
					});
				}
				break;
			case "removeFromGroup":
				{
					const { entityType, id, parentGroupId, name } = node || selectedNode || {};

					const devices = [{ type: entityType, id: id }];
					const body = { devices };
					deviceGroupsService.removeDevices(parentGroupId, body, (err, res) => {
						if (err || res?.success === false) {
							const errorMessage = {
								message: getTranslation(
									"global.deviceManager.deviceTree.failedRemovingFromDeviceGroup",
									{ primaryValue: name }
								),
								error: err
							};
							console.error("Error Details:", errorMessage);
							dispatch(
								createUserFeedback(
									getTranslation("global.deviceManager.deviceTree.errorRemovingFromDeviceGroup", {
										name: name
									}),
									null,
									true
								)
							);
							return;
						}

						dispatch(
							createUserFeedback(
								getTranslation("global.deviceManager.deviceTree.deviceRemovedFromGroupSuccess", {
									name: name
								}),
								null,
								false
							)
						);
					});
				}
				break;
			default:
				return;
		}
		setContextMenu(null);
	};

	const handleNodeToggle = (event, nodeIds) => {
		setExpandedNodes(nodeIds);
	};

	const handleExpandNodes = (id) => {
		setExpandedNodes([...expandedNodes, id]);
	};

	useEffect(() => {
		if (expandRootNodeByDefault && expandedNodes.length === 0) setExpandedNodes(["root"]);
	}, [expandRootNodeByDefault]);

	useEffect(() => {
		if (!selectedNodeForEdit) return;

		const findParentChain = (nodes, targetId, parents = []) => {
			for (const node of nodes) {
				if (node.id === targetId) return parents;
				if (node.children?.length) {
					const result = findParentChain(node.children, targetId, [...parents, node.id]);
					if (result) return result;
				}
			}
			return null;
		};

		const parentChain = findParentChain(deviceGroups, selectedNodeForEdit);

		// If any parent is not expanded, close the naming field
		if (parentChain && parentChain.some((parentId) => !expandedNodes.includes(parentId))) {
			// Remove blank node
			if (addingNewNode) {
				dispatch(deleteDeviceGroup(selectedNodeForEdit));
				setAddingNewNode(false);
			}

			setSelectedNodeForEdit(null);
		}
	}, [expandedNodes, selectedNodeForEdit, deviceGroups, addingNewNode]);

	const handleRightClick = (event, node) => {
		if (isForFacilitiesFloorPlan) return;
		event.preventDefault();

		// Adjust the position to ensure the menu does not overflow the viewport
		const menuWidth = 210; // Approximate width of your menu
		const menuHeight = 210; // Approximate height of your menu

		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		const adjustedX =
			event.clientX + menuWidth > viewportWidth
				? viewportWidth - menuWidth - 10 // Ensure menu stays within viewport
				: event.clientX - 20;

		const adjustedY =
			event.clientY + menuHeight > viewportHeight
				? viewportHeight - menuHeight - 10 // Ensure menu stays within viewport
				: event.clientY - 55;

		setContextMenu({
			mouseX: adjustedX,
			mouseY: adjustedY
		});

		const deviceCommandsMenuItem = [];

		const generateCommandsMenuItem = (node) => {
			if (node?.isGroup && node?.deviceCommands) {
				node?.deviceCommands?.forEach((command, index) =>
					deviceCommandsMenuItem.push({
						id: `command_menu_${index}`,
						canAccessMenu: true,
						item: command.label,
						action: () => {
							command?.entityIds?.forEach((entityId) => {
								dispatch(controlClicked(command, entityId));
							});
						},
						hasNestedMenu: false
					})
				);
			} else if (node?.entityType === "compositeDevice") {
				node?.deviceCommands?.forEach((command, index) =>
					deviceCommandsMenuItem.push({
						id: `command_menu_${index}`,
						canAccessMenu: true,
						item: command.label,
						action: () => {
							node?.children?.forEach((child) => {
								if (command?.entityIds?.includes(child?.id)) {
									dispatch(controlClicked(command, child.id));
								}
							});
						},

						hasNestedMenu: false
					})
				);
			} else {
				node?.entityData?.properties?.commands?.forEach((command, index) => {
					deviceCommandsMenuItem.push({
						id: "command_menu_" + index,
						canAccessMenu: true,
						item: command.label,
						action: () => {
							dispatch(controlClicked(command, node.id));
						},
						hasNestedMenu: false
					});
				});
			}
		};

		if (node.isGroup) {
			setRightClickFor("group");
			if (allowDeviceGroupCommands) {
				generateCommandsMenuItem(node);
				setDeviceGroupRolledUpCommands(uniqBy(deviceCommandsMenuItem, "id"));
			}
		} else if (node?.entityType === "compositeDevice") {
			setRightClickFor("compositeDevice");
			generateCommandsMenuItem(node);
			setCompositeDeviceRolledUpCommands(uniqBy(deviceCommandsMenuItem, "id"));
		} else {
			setRightClickFor("basicDevice");
			generateCommandsMenuItem(node);
			setBasicDeviceCommands(uniqBy(deviceCommandsMenuItem, "id"));
		}
		setSelectedNode(node);
		dispatch(setFields({ selectedRows: [node] }));
	};

	const addOrUpdateNodeLabel = (nodeId, updateData) => {
		if (addingNewNode) {
			deviceGroupsService.create(
				{ id: nodeId, name: updateData.name, parentGroupId: updateData.parentGroupId, devices: [] },
				(err) => {
					if (err) {
						const errorMessage = {
							message: "Failed to create a device group",
							error: err
						};
						console.error("Error Details:", errorMessage);
						dispatch(deleteDeviceGroup(nodeId));
						return;
					}
					dispatch(updateDeviceGroup(nodeId, updateData));
				}
			);
			setAddingNewNode(false);
			setSelectedNodeForEdit(null);
		} else {
			deviceGroupsService.update(nodeId, updateData, (err) => {
				if (err) {
					const errorMessage = {
						message: "Failed to update a device group",
						error: err
					};
					console.error("Error Details:", errorMessage);
					return;
				}
				dispatch(updateDeviceGroup(nodeId, updateData));
				setSelectedNodeForEdit(null);
			});
		}
	};

	const addNewNode = (parentId) => {
		dispatch(createDeviceGroup(parentId, setSelectedNodeForEdit));
		setExpandedNodes((prevExpanded) => [...new Set([...prevExpanded, parentId])]); // Expand parent node
		setAddingNewNode(true);
	};

	useEffect(() => {
		if (!autoExpandMatchingNodes && !autoExpandNodes) return;
		if ((search || autoExpandNodes) && deviceGroups?.length) {
			// Expanding all nodes and their parent nodes if they match the search string
			const nodesToExpand = [];
			const getNodesToExpand = (node) => {
				if (node.id) nodesToExpand.push(node.id);
				node?.children?.map(getNodesToExpand);

				return nodesToExpand;
			};

			const expandedNodes = uniq(getNodesToExpand(deviceGroups[0]));
			setExpandedNodes(expandedNodes);
		} else {
			setExpandedNodes([]);
		}
	}, [search, autoExpandNodes, deviceGroups]);

	useEffect(() => {
		if (addingNewNode && selectedNodeForEdit) {
			const nodeStillExists = (nodes) => {
				for (const node of nodes) {
					if (node.id === selectedNodeForEdit) return true;
					if (node.children && nodeStillExists(node.children)) return true;
				}
				return false;
			};

			if (!nodeStillExists(deviceGroups)) {
				setAddingNewNode(false);
				setSelectedNodeForEdit(null);
			}
		}
	}, [deviceGroups, addingNewNode, selectedNodeForEdit]);

	return (
		<div style={{ ...customStyles, height: "100%" }}>
			<Box sx={{ height: "100%" }}>
				<CustomizedTreeView expanded={expandedNodes} onNodeToggle={handleNodeToggle} sx={styles.treeSx}>
					{deviceGroups.length > 0 ? (
						deviceGroups.map((node) => (
							<Box
								key={node?.id}
								sx={{
									height: "100%",
									minWidth: 300,
									overflowX: "auto",
									"&::-webkit-scrollbar": {
										height: "13px"
									}
								}}
							>
								<RecursiveTreeView
									key={node?.id}
									nodes={[node]} // Pass as an array
									expandedNodes={expandedNodes}
									addOrUpdateNodeLabel={addOrUpdateNodeLabel}
									addNewNode={addNewNode}
									selectedNodeForEdit={selectedNodeForEdit} // Pass to enable editing
									setSelectedNodeForEdit={setSelectedNodeForEdit}
									onContextMenu={handleRightClick}
									addToDeviceGroup={addToDeviceGroup}
									readOnly={readOnly}
									getSelectedNodes={getSelectedNodes}
									isForFacilitiesFloorPlan={isForFacilitiesFloorPlan}
									getSelectedDevice={getSelectedDevice}
									getSelectedMenuItem={getSelectedMenuItem}
									allowDeviceSelection={allowDeviceSelection}
								/>
							</Box>
						))
					) : (
						<Typography
							style={{
								padding: "24px 0px",
								color: "#B5B9BE",
								display: "inherit",
								border: "1px solid #707070",
								borderRadius: "10px",
								height: "143px",
								margin: "23px 0px",
								display: "flex",
								alignItems: "center"
							}}
							align="center"
							variant="caption"
							sx={{ color: "#98999D" }}
						>
							<Translate
								value="global.deviceManager.deviceTree.noDevices"
								style={{ width: "100%", fontSize: 14, color: "#B4B8BC" }}
							/>
						</Typography>
					)}
				</CustomizedTreeView>

				{/* Render the RightClickMenu */}
				{contextMenu && canManageCamerasApp && !isForFacilitiesFloorPlan && (
					<RightClickMenu
						contextMenu={contextMenu}
						onClose={() => setContextMenu(null)}
						node={selectedNode}
						menuFor={rightClickFor}
						getSelectedMenuItem={getSelectedMenuItem}
						compositeDeviceRolledUpCommands={compositeDeviceRolledUpCommands}
						selectedRows={selectedRows}
						basicDeviceCommands={basicDeviceCommands}
						readOnly={readOnly}
						deviceGroupRolledUpCommands={deviceGroupRolledUpCommands}
					/>
				)}
			</Box>
		</div>
	);
};

DeviceTree.propTypes = {
	expandRootNodeByDefault: PropTypes.bool,
	addToDeviceGroup: PropTypes.func.isRequired,
	getSelectedNodes: PropTypes.func,
	readOnly: PropTypes.bool,
	selectedBatchColors: PropTypes.array,
	search: PropTypes.string,
	isForFacilitiesFloorPlan: PropTypes.bool,
	getSelectedDevice: PropTypes.func,
	setFields: PropTypes.func,
	entitySelectionFilters: PropTypes.object,
	autoExpandMatchingNodes: PropTypes.bool,
	customStyles: PropTypes.object,
	addUngroupedDevicesToRoot: PropTypes.bool
};

DeviceTree.defaultProps = {
	getSelectedNodes: () => {},
	readOnly: false,
	selectedBatchColors: [],
	search: "",
	isForFacilitiesFloorPlan: false,
	getSelectedDevice: () => {},
	setFields: () => {},
	entitySelectionFilters: {},
	customStyles: {},
	expandRootNodeByDefault: false
};

export default DeviceTree;
